/**
 * Rasterising an uploaded branding image into the fixed-size square stored in
 * the `branding` config key.
 *
 * Split deliberately: the geometry and the SVG normalisation are pure and
 * unit-tested, while {@link decodeImageFile} and {@link drawBrandingImage} are
 * thin wrappers over `<img>` and `<canvas>`. Tests run on jsdom without the
 * `canvas` package, so `getContext('2d')` yields nothing there and the drawing
 * half cannot execute under vitest — which is why the arithmetic lives outside
 * it and why callers take the draw step as an injected dependency.
 */

/** Edge length of the square persisted to config. */
export const BRANDING_STORED_SIZE = 128;

/**
 * Largest file we will attempt to decode. Checked *before* decoding: a
 * pathological image (say 20000x20000) can exhaust memory during decode, well
 * before a canvas is involved, and a byte-size gate is the only cheap check
 * that runs earlier.
 */
export const BRANDING_MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Largest encoded data URI we will store. `GET /config` returns every config
 * row in one payload, so anything kept here is paid for by every consumer of
 * that endpoint.
 *
 * Sized to be UNREACHABLE by anything this pipeline produces, so the cap is a
 * guard against a hand-written config row rather than a limit a user can hit
 * by choosing a busy picture. A {@link BRANDING_STORED_SIZE} square of RGBA is
 * 64 KB raw, so incompressible content gives ~66 KB of PNG and ~88 KB once
 * base64 inflates it by 4/3. An earlier 64 KB cap was inside that range — a
 * photographic square measured 38.7 KB in practice — which made "use a simpler
 * image" a dead end for a legitimately processed file.
 */
export const BRANDING_MAX_ENCODED_BYTES = 128 * 1024;

/** Input formats we accept. SVG is rasterised like the rest; nothing is stored as SVG. */
export const BRANDING_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'] as const;

export const BRANDING_ACCEPT_ATTRIBUTE = BRANDING_ACCEPTED_TYPES.join(',');

/**
 * How a non-square source is mapped onto the square.
 *
 * - `contain` fits the whole image inside the square, padding with
 *   transparency — nothing is ever cut off.
 * - `cover` centre-crops to fill the square.
 */
export type BrandingImageFit = 'contain' | 'cover';

export interface Size {
  readonly width: number;
  readonly height: number;
}

export interface Rect extends Size {
  readonly x: number;
  readonly y: number;
}

/**
 * Which region of the source is sampled, and where it lands on the square.
 *
 * INVARIANT: `source` and `dest` always share an aspect ratio — `cover` crops
 * the source to the square's 1:1, `contain` fits the destination to the
 * source's aspect. Downscaling is therefore uniform in both axes, which is what
 * lets {@link computeHalvingSteps} reason about a single scale factor.
 */
export interface DrawPlan {
  readonly source: Rect;
  readonly dest: Rect;
}

const assertUsableSize = ({ width, height }: Size): void => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error(`Unusable image dimensions: ${width}x${height}`);
  }
};

/**
 * Map a decoded image of `source` size onto a `target`-sided square.
 *
 * Sub-target sources ARE scaled up, which looks wrong written down but is
 * right: `target` is a 2x buffer for a square rendered at half that size, so a
 * 64x64 source drawn to 128 and displayed at 64 is not magnified on screen at
 * all. Refusing to upscale would instead shrink such an image inside the bar.
 */
export const computeDrawPlan = (source: Size, fit: BrandingImageFit, target: number = BRANDING_STORED_SIZE): DrawPlan => {
  assertUsableSize(source);
  assertUsableSize({ width: target, height: target });

  if (fit === 'cover') {
    const side = Math.min(source.width, source.height);
    return {
      source: { x: (source.width - side) / 2, y: (source.height - side) / 2, width: side, height: side },
      dest: { x: 0, y: 0, width: target, height: target },
    };
  }

  const scale = Math.min(target / source.width, target / source.height);
  const width = source.width * scale;
  const height = source.height * scale;
  return {
    source: { x: 0, y: 0, width: source.width, height: source.height },
    dest: { x: (target - width) / 2, y: (target - height) / 2, width, height },
  };
};

/**
 * How many intermediate halvings to take before the final draw.
 *
 * A single `drawImage` across a large reduction aliases visibly in some
 * engines; halving repeatedly and drawing the last step at a ratio under 2
 * does not. Returns 0 whenever the reduction is already gentle (or is an
 * upscale), in which case one direct draw is correct.
 */
export const computeHalvingSteps = (source: Size, dest: Size): number => {
  assertUsableSize(source);
  assertUsableSize(dest);

  let width = source.width;
  let height = source.height;
  let steps = 0;

  while (Math.floor(width / 2) >= dest.width && Math.floor(height / 2) >= dest.height) {
    width = Math.floor(width / 2);
    height = Math.floor(height / 2);
    steps += 1;
  }

  return steps;
};

/**
 * Give an SVG explicit `width`/`height` attributes when it has none.
 *
 * An SVG carrying only a `viewBox` has no intrinsic size, and such a document
 * decodes to a zero-sized image in some engines — which would otherwise
 * produce a silently blank square rather than an error. Dimensions are taken
 * from the `viewBox` when present so the aspect ratio survives, and fall back
 * to a `fallback`-sided square when it does not. Documents that already
 * declare both attributes are returned untouched.
 */
export const ensureSvgIntrinsicSize = (svgText: string, fallback: number = BRANDING_STORED_SIZE * 4): string => {
  const parsed = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const svg = parsed.documentElement;

  if (svg.nodeName !== 'svg' || parsed.getElementsByTagName('parsererror').length > 0) {
    throw new Error('The selected file is not a readable SVG document.');
  }

  const hasWidth = svg.getAttribute('width') !== null;
  const hasHeight = svg.getAttribute('height') !== null;
  if (hasWidth && hasHeight) return svgText;

  const viewBox = (svg.getAttribute('viewBox') ?? '')
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const usableViewBox = viewBox.length === 4 && viewBox.every((n) => Number.isFinite(n)) && viewBox[2] > 0 && viewBox[3] > 0;

  if (!hasWidth) svg.setAttribute('width', String(usableViewBox ? viewBox[2] : fallback));
  if (!hasHeight) svg.setAttribute('height', String(usableViewBox ? viewBox[3] : fallback));

  return new XMLSerializer().serializeToString(svg);
};

/**
 * Pull the stored square out of an unknown `branding` config value.
 *
 * ONE tolerant reading, shared by the editor (reading the modal's form state)
 * and the navbar selector (reading the redux row), so the two cannot disagree
 * about what an absent, null or malformed value means. Anything that is not a
 * data URI reads as "no image" rather than reaching an `<img src>`.
 */
export const readBrandingImageValue = (value: unknown): string | null => {
  const image = (value as { image?: unknown } | null | undefined)?.image;
  return typeof image === 'string' && image.startsWith('data:image/') ? image : null;
};

/** A decoded source image, plus the object URL that must be released after use. */
export interface DecodedImage {
  readonly element: HTMLImageElement;
  readonly size: Size;
  readonly release: () => void;
}

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The selected file could not be read.'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsText(file);
  });

const decodeObjectUrl = async (blob: Blob): Promise<DecodedImage> => {
  const url = URL.createObjectURL(blob);
  const element = new Image();
  const release = () => URL.revokeObjectURL(url);

  element.src = url;
  try {
    await element.decode();
  } catch {
    release();
    throw new Error('The selected file could not be decoded as an image.');
  }

  const size = { width: element.naturalWidth, height: element.naturalHeight };
  if (size.width <= 0 || size.height <= 0) {
    release();
    throw new Error('The selected image reports no usable dimensions.');
  }

  return { element, size, release };
};

/**
 * Decode `file` into something drawable, rejecting what must not reach a canvas.
 *
 * Decoding goes through `<img>` rather than `createImageBitmap`, which does not
 * handle SVG blobs portably; one decode path covers every accepted format. It
 * also means the browser applies a JPEG's EXIF orientation flag for us, so a
 * photo taken in portrait is not stored on its side.
 */
export const decodeImageFile = async (file: File): Promise<DecodedImage> => {
  if (file.size > BRANDING_MAX_FILE_BYTES) {
    throw new Error(`The image is larger than ${Math.round(BRANDING_MAX_FILE_BYTES / (1024 * 1024))} MB. Choose a smaller file.`);
  }
  if (!BRANDING_ACCEPTED_TYPES.some((type) => type === file.type)) {
    throw new Error('Unsupported image format. Use PNG, JPEG, GIF, WebP or SVG.');
  }

  if (file.type === 'image/svg+xml') {
    const normalised = ensureSvgIntrinsicSize(await readFileAsText(file));
    return decodeObjectUrl(new Blob([normalised], { type: 'image/svg+xml' }));
  }

  return decodeObjectUrl(file);
};

/**
 * Draw a decoded image onto a detached canvas and return it as a PNG data URI.
 *
 * The canvas is never appended to the document — no mount, no layout, no
 * paint, and React never learns it exists. PNG keeps the alpha channel, and the
 * canvas is deliberately left unfilled so a logo with a transparent background
 * sits on the navbar's own colour instead of a white box.
 */
export const drawBrandingImage = (source: DecodedImage, plan: DrawPlan, target: number = BRANDING_STORED_SIZE): string => {
  const steps = computeHalvingSteps(plan.source, plan.dest);

  let current: HTMLCanvasElement | HTMLImageElement = source.element;
  let currentRect: Rect = plan.source;

  for (let step = 0; step < steps; step += 1) {
    const halved = document.createElement('canvas');
    halved.width = Math.floor(currentRect.width / 2);
    halved.height = Math.floor(currentRect.height / 2);
    const halvedContext = halved.getContext('2d');
    if (!halvedContext) throw new Error('This browser did not provide a 2D canvas context.');
    halvedContext.imageSmoothingEnabled = true;
    halvedContext.imageSmoothingQuality = 'high';
    halvedContext.drawImage(current, currentRect.x, currentRect.y, currentRect.width, currentRect.height, 0, 0, halved.width, halved.height);
    current = halved;
    currentRect = { x: 0, y: 0, width: halved.width, height: halved.height };
  }

  const canvas = document.createElement('canvas');
  canvas.width = target;
  canvas.height = target;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser did not provide a 2D canvas context.');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(current, currentRect.x, currentRect.y, currentRect.width, currentRect.height, plan.dest.x, plan.dest.y, plan.dest.width, plan.dest.height);

  return canvas.toDataURL('image/png');
};
